import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "deleteConfirmModal", "userSpan", "projectSpan", "dateSpan", "milestoneSpan", "milestoneInfo", "milestoneSelect", "descriptionTextarea", "saveBtn", "deleteBtn", "confirmDeleteBtn"]
  
  connect() {
    this.currentEvent = null
    this.projectMilestones = {}
    this.setupEventHandlers()
  }

  setupEventHandlers() {
    // Remove existing event listeners to prevent duplicates
    this.saveBtnTarget.replaceWith(this.saveBtnTarget.cloneNode(true))
    this.deleteBtnTarget.replaceWith(this.deleteBtnTarget.cloneNode(true))
    this.confirmDeleteBtnTarget.replaceWith(this.confirmDeleteBtnTarget.cloneNode(true))
    
    // Get fresh references
    const saveBtn = document.getElementById('saveAssignmentBtn')
    const deleteBtn = document.getElementById('deleteAssignmentBtn')
    const confirmDeleteBtn = document.getElementById('confirmDeleteBtn')
    
    // Save assignment changes
    saveBtn.addEventListener('click', async () => {
      await this.saveChanges()
    })
    
    // Show delete confirmation
    deleteBtn.addEventListener('click', () => {
      this.showDeleteConfirmation()
    })
    
    // Confirm delete
    confirmDeleteBtn.addEventListener('click', async () => {
      await this.confirmDelete()
    })
  }

  async show(event, projectName) {
    this.currentEvent = event
    const eventData = event.extendedProps
    const assignmentDate = event.start ? event.start.toLocaleDateString() : 'Unknown Date'
    
    // Extract user name without milestone info or emoji
    let userName = event.title
    userName = userName.replace(' 📝', '') // Remove description emoji
    userName = userName.replace(/\s*\(.*\)/, '') // Remove milestone name in parentheses
    
    // Populate modal with assignment data
    this.userSpanTarget.textContent = userName
    this.projectSpanTarget.textContent = projectName
    this.dateSpanTarget.textContent = assignmentDate
    this.descriptionTextareaTarget.value = eventData.description || ''
    
    // Load and populate milestone options for this project
    if (eventData.projectSlug) {
      await this.loadMilestones(eventData.projectSlug)
      this.populateMilestoneDropdown(eventData.projectSlug, eventData.milestoneId)
    }
    
    // Show the modal
    this.modalTarget.showModal()
  }
  
  async loadMilestones(projectSlug) {
    // Return cached milestones if already loaded
    if (this.projectMilestones[projectSlug]) {
      return
    }
    
    try {
      const response = await fetch(`/projects/${projectSlug}/milestones.json`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      
      if (response.ok) {
        const milestones = await response.json()
        this.projectMilestones[projectSlug] = milestones
      } else {
        this.projectMilestones[projectSlug] = []
      }
    } catch (error) {
      console.error('Error loading milestones:', error)
      this.projectMilestones[projectSlug] = []
    }
  }
  
  populateMilestoneDropdown(projectSlug, currentMilestoneId) {
    const select = this.milestoneSelectTarget
    const milestones = this.projectMilestones[projectSlug] || []
    
    // Clear existing options except the first (project-level)
    while (select.options.length > 1) {
      select.remove(1)
    }
    
    // Add milestone options
    milestones.forEach(milestone => {
      const option = document.createElement('option')
      option.value = milestone.id
      option.textContent = milestone.name
      select.appendChild(option)
    })
    
    // Set selected value
    select.value = currentMilestoneId || ''
  }

  showDeleteConfirmation() {
    if (!this.currentEvent) return
    
    const projectName = this.projectSpanTarget.textContent
    const milestoneName = this.currentEvent.extendedProps.milestoneName
    
    // Populate delete confirmation modal
    document.getElementById('deleteConfirmUser').textContent = this.userSpanTarget.textContent
    document.getElementById('deleteConfirmProject').textContent = projectName
    
    // Show milestone info if present
    const milestoneSpan = document.getElementById('deleteConfirmMilestone')
    if (milestoneName) {
      milestoneSpan.textContent = ` (${milestoneName})`
    } else {
      milestoneSpan.textContent = ''
    }
    
    // Hide edit modal and show confirmation modal
    this.modalTarget.close()
    this.deleteConfirmModalTarget.showModal()
  }

  async saveChanges() {
    if (!this.currentEvent) return
    
    const description = this.descriptionTextareaTarget.value
    const milestoneId = this.milestoneSelectTarget.value || null
    const eventData = this.currentEvent.extendedProps
    
    try {
      const requestBody = {
        assignment_id: eventData.assignmentId,
        description: description,
        milestone_id: milestoneId
      }
      
      // Include assignment_ids for multi-day assignments
      if (eventData.assignmentIds && Array.isArray(eventData.assignmentIds)) {
        requestBody.assignment_ids = eventData.assignmentIds
      }
      
      const response = await fetch('/team/update_assignment_description', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Update response not ok:', response.status, errorText)
        throw new Error(`Server error: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.success) {
        // Update the event's extended props with new data
        this.currentEvent.setExtendedProp('description', description)
        this.currentEvent.setExtendedProp('milestoneId', data.milestone_id)
        this.currentEvent.setExtendedProp('milestoneName', data.milestone_name)
        
        // Update the title to include/remove emoji and milestone info
        const userName = this.userSpanTarget.textContent
        let newTitle = userName
        if (data.milestone_name) {
          newTitle += ` (${data.milestone_name})`
        }
        if (description) {
          newTitle += ' 📝'
        }
        this.currentEvent.setProp('title', newTitle)
        
        this.showToast(data.message, 'success')
        this.modalTarget.close()
      } else {
        this.showToast(`Error: ${data.errors.join(', ')}`, 'error')
      }
    } catch (error) {
      console.error('Error updating assignment:', error)
      this.showToast('Error updating assignment', 'error')
    }
  }

  async confirmDelete() {
    if (!this.currentEvent) return
    
    const eventData = this.currentEvent.extendedProps
    
    try {
      const response = await this.deleteAssignment(
        eventData.assignmentId,
        eventData.assignmentIds
      )
      
      if (response.success) {
        this.currentEvent.remove()
        this.currentEvent = null
        this.deleteConfirmModalTarget.close()
        this.showToast('Assignment removed', 'success')
        
        // Dispatch event to notify parent controller that assignment was deleted
        this.dispatch('assignment-deleted')
      } else {
        this.showToast(`Error: ${response.errors.join(', ')}`, 'error')
      }
    } catch (error) {
      console.error('Error deleting assignment:', error)
      this.showToast('Error removing assignment', 'error')
    }
  }

  async deleteAssignment(assignmentId, assignmentIds = null) {
    try {
      const requestBody = {
        assignment_id: assignmentId
      }
      
      // Include assignment_ids for multi-day assignments
      if (assignmentIds && Array.isArray(assignmentIds)) {
        requestBody.assignment_ids = assignmentIds
      }
      
      const response = await fetch('/team/delete_assignment', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Delete response not ok:', response.status, errorText)
        return { success: false, errors: [`Server error: ${response.status}`] }
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error deleting assignment:', error)
      return { success: false, errors: ['Network error occurred'] }
    }
  }

  showToast(message, type = 'info') {
    // Try to dispatch a custom event that the parent controller can handle
    this.dispatch('toast', { detail: { message, type } })
  }
}